import { useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, FileArchive, X } from 'lucide-react';
import { csr, studies as studiesApi } from '@/api/client';
import { toast } from 'sonner';
import { errorText } from '@/lib/permissionError';
import { studyLabel } from '@/lib/studyLabel';

/**
 * DREAM-15 — re-usable "Export CSR Prep" modal.
 *
 * Fetches studies for the active site so the user picks from a dropdown
 * instead of typing a raw UUID. Re-auth gate fires only when PHI is included.
 *
 * The export builds synchronously inside POST /csr/exports (no queue/worker),
 * so by the time `create` resolves the job is already `ready` (or `failed`).
 * We chain straight into the download call using the SAME password already
 * entered for the PHI re-auth gate — no separate exports page/history view,
 * no second password prompt.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  defaultStudyId?: string;
  /** CTMS-009 — pre-tick the de-identified checkbox when set on the page. */
  defaultDeidentified?: boolean;
  onCreated?: (jobId: string) => void;
}

function triggerBlobDownload(fileB64: string, jobId: string) {
  const bin = atob(fileB64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `csr-export-${jobId}.zip`;
  a.click();
  URL.revokeObjectURL(url);
}

function triggerUrlDownload(downloadUrl: string) {
  const a = document.createElement('a');
  a.href = downloadUrl;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export function CsrCreateModal({
  open,
  onClose,
  defaultStudyId = '',
  defaultDeidentified = false,
  onCreated,
}: Props) {
  const [draft, setDraft] = useState({
    study_id: defaultStudyId,
    include_phi: false,
    password: '',
    deidentified: defaultDeidentified,
  });

  const studyList = useQuery({
    queryKey: ['studies'],
    queryFn: () => studiesApi.list(),
    enabled: open,
    staleTime: 60_000,
  });

  const download = useMutation({
    mutationFn: (input: { job_id: string; password?: string }) =>
      csr.download({ job_id: input.job_id, reauth_password: input.password }),
    onSuccess: (data, { job_id }) => {
      if (data.file_b64) triggerBlobDownload(data.file_b64, job_id);
      else if (data.download_url) triggerUrlDownload(data.download_url);
      toast.success('CSR export downloaded');
    },
    onError: (err) =>
      toast.error(
        errorText(err, 'Export was created but the download failed. Try again shortly.'),
      ),
  });

  const create = useMutation({
    mutationFn: () =>
      csr.create({
        study_id: draft.study_id,
        include_phi: draft.include_phi,
        deidentified: draft.deidentified,
        ...(draft.include_phi ? { reauth_password: draft.password } : {}),
      }),
    onSuccess: (res) => {
      toast.success('CSR export generated — downloading…');
      const password = draft.include_phi ? draft.password : undefined;
      setDraft({
        study_id: defaultStudyId,
        include_phi: false,
        password: '',
        deidentified: defaultDeidentified,
      });
      onCreated?.(res.job_id);
      onClose();
      download.mutate({ job_id: res.job_id, password });
    },
    onError: (err) =>
      toast.error(errorText(err, 'Failed to create export')),
  });

  function handleClose() {
    setDraft({
      study_id: defaultStudyId,
      include_phi: false,
      password: '',
      deidentified: defaultDeidentified,
    });
    onClose();
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (create.isPending) return;
    if (!draft.study_id) return;
    if (draft.include_phi && !draft.password) return;
    create.mutate();
  }

  if (!open) return null;

  const rows = studyList.data?.rows ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center !m-0 bg-black/60 backdrop-blur-sm p-4 !m-0">
      <form onSubmit={handleSubmit} className="card w-full max-w-md space-y-4 p-6">
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-semibold text-ink-primary">New CSR export</h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={create.isPending}
            className="text-ink-muted hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-xs text-ink-muted">
          Bundles eCRF data, audit trail, query log, and protocol deviations into a structured ZIP
          (PDF + CSV). 21 CFR Part 11 re-auth required for PHI exports.
        </p>

        {/* Study picker */}
        <div>
          <label className="label text-xs" htmlFor="csr-modal-study">
            Study *
          </label>
          {studyList.isLoading && (
            <div className="flex items-center gap-2 py-2 text-xs text-ink-muted">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading studies…
            </div>
          )}
          {studyList.isError && <p className="text-xs text-danger-600">Failed to load studies.</p>}
          {!studyList.isLoading && rows.length === 0 && !studyList.isError && (
            <p className="text-xs text-ink-muted">No studies found for this site.</p>
          )}
          {rows.length > 0 && (
            <select
              id="csr-modal-study"
              className="input"
              required
              disabled={create.isPending}
              value={draft.study_id}
              onChange={(e) => setDraft({ ...draft, study_id: e.target.value })}
            >
              <option value="">Select a study…</option>
              {rows.map((s) => (
                <option key={s.study_id} value={s.study_id}>
                  {studyLabel(s)}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* PHI toggle */}
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.include_phi}
            disabled={create.isPending}
            onChange={(e) => setDraft({ ...draft, include_phi: e.target.checked })}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium">Include PHI</span>
            <span className="ml-1 text-xs text-ink-muted">(regulator-only)</span>
          </span>
        </label>

        {/* CTMS-009 — de-identified toggle */}
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.deidentified}
            disabled={create.isPending}
            onChange={(e) => setDraft({ ...draft, deidentified: e.target.checked })}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium">De-identified export (PHI removed)</span>
            <span className="ml-1 text-xs text-ink-muted">
              (strips full name + DOB, tokenises subject ref)
            </span>
          </span>
        </label>

        {/* Re-auth password — only required for PHI exports */}
        {draft.include_phi && (
          <div className="space-y-2">
            <div className="rounded-md border border-warning-200 bg-warning-50 px-3 py-2 text-xs text-warning-800">
              <span className="font-semibold">21 CFR Part 11 — Electronic Signature</span>
              <p className="mt-0.5">
                Including PHI requires identity verification. Entering your password constitutes a
                legally binding electronic signature — equivalent to a handwritten signature —
                confirming you are authorised to request a PHI export for this study.
              </p>
            </div>
            <label className="label text-xs" htmlFor="csr-modal-pass">
              Your account password *
            </label>
            <input
              id="csr-modal-pass"
              type="password"
              required
              disabled={create.isPending}
              autoComplete="current-password"
              className="input"
              value={draft.password}
              onChange={(e) => setDraft({ ...draft, password: e.target.value })}
            />
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            className="btn-ghost"
            onClick={handleClose}
            disabled={create.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={create.isPending || !draft.study_id || (draft.include_phi && !draft.password)}
          >
            {create.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileArchive className="h-4 w-4" />
            )}
            Start export
          </button>
        </div>
      </form>
    </div>
  );
}
